import DetailCompositionController from "../../../../API/organization/DetailCompositionController";
import {flattenModuleCompositionRow} from "../../../../utils/flattenModuleCompositionRow";
import {
    getPacedDetailModuleId,
    parsePacedDetailModuleDto,
    stampCompositionDetailId,
    stampCompositionDetailList,
} from "./pacedDetailModule";
import {
    getPacedDtoHide,
    getPacedModuleId,
    parsePacedModuleDto,
} from "./pacedModule";
import {moduleDimensionsMmToSizeM} from "./snapPlacement";
import {axesToEulerDegrees, IDENTITY_AXES} from "./orientationAxes";
import {
    DEFAULT_TEXTURE_ROTATION_DEG,
    DEFAULT_TEXTURE_SCALE_MM,
} from "./detailTextures";
import {
    getDetailSceneDrawOrder,
    sortDetailsBySequenceNumber,
} from "../../../../utils/processing/orderContourPoints";

export const getDependModuleKey = (row, index = 0) =>
    row?.id ?? `depend-module-${index}`;

const hasRealId = (value) => value != null && value !== "" && Number(value) !== 0;

/** Строка dependModules → единый вид (moduleId для API). */
export const normalizeDependModuleRow = (row) => {
    if (row == null || typeof row !== "object") return row;
    if (hasRealId(row.moduleId)) return row;
    if (row.module != null && typeof row.module === "object" && hasRealId(row.module.id)) {
        return flattenModuleCompositionRow(row);
    }
    return row;
};

/** id модуля для API (by-module-id, paced-modules) — не id связи в составе. */
export const getDependModuleId = (row) => {
    const normalized = normalizeDependModuleRow(row);
    const moduleId = normalized?.moduleId ?? normalized?.module?.id;
    if (hasRealId(moduleId)) return moduleId;
    return hasRealId(normalized?.id) ? normalized.id : null;
};

const normalizeModuleDimension = (value) => (
    value == null || value === "" ? null : Number(value)
);

/** Изменились ли габариты модуля (ширина / высота / глубина). */
export const moduleDimensionsChanged = (before, after) => (
    ["width", "height", "depth"].some(
        (key) => normalizeModuleDimension(before?.[key]) !== normalizeModuleDimension(after?.[key]),
    )
);

const getEmbeddedDetailKey = (detail, index) =>
    detail?.compositionDetailId ?? detail?.id ?? detail?.position ?? `detail-${index}`;

const embeddedDetailsCache = new Map();

export const invalidateEmbeddedModuleDetailsCache = (dependModuleId) => {
    if (dependModuleId == null || dependModuleId === "") return;
    embeddedDetailsCache.delete(String(dependModuleId));
};

/**
 * GET by-module-id — тот же запрос, что на 3D-вкладке самого встраиваемого модуля.
 * @returns {{details: Array, loaded: boolean, notFound: boolean}}
 */
export const loadEmbeddedModuleDetails = async (dependModuleId, {bypassCache = false} = {}) => {
    if (dependModuleId == null || dependModuleId === "") {
        return {details: [], loaded: false, notFound: false};
    }
    const cacheKey = String(dependModuleId);
    if (!bypassCache) {
        const cached = embeddedDetailsCache.get(cacheKey);
        if (
            cached
            && typeof cached === "object"
            && !Array.isArray(cached)
            && Array.isArray(cached.details)
        ) {
            return cached;
        }
    }

    try {
        const response = await DetailCompositionController.byModuleId(dependModuleId);
        const raw = Array.isArray(response?.data) ? response.data : [];
        const details = stampCompositionDetailList(raw);
        const result = {details, loaded: true, notFound: false};
        embeddedDetailsCache.set(cacheKey, result);
        return result;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) {
            // 404 не кэшируем — иначе второй запрос не уходит, хотя id мог быть неверным.
            return {details: [], loaded: false, notFound: true};
        }
        throw err;
    }
};

/** Деталь встраиваемого модуля с paced-позой → mesh внутри его куба. */
export const buildEmbeddedChildFromDetail = (detail, index = 0) => {
    const row = stampCompositionDetailId(detail);
    const paced = parsePacedDetailModuleDto(row?.pacedDetailModule);
    if (!row || !paced?.localPosition || !paced?.axes) return null;

    const detailKey = getEmbeddedDetailKey(row, index);
    const pacedId = getPacedDetailModuleId(row);

    return {
        instanceId: pacedId != null ? `embedded-child-${pacedId}` : `embedded-child-${detailKey}`,
        detail: row,
        detailData: row,
        detailKey,
        localPosition: paced.localPosition,
        axes: paced.axes,
        faceFlip: Boolean(paced.faceFlip),
        textureUrl: null,
        textureScaleMm: DEFAULT_TEXTURE_SCALE_MM,
        textureRotationDeg: DEFAULT_TEXTURE_ROTATION_DEG,
    };
};

/** Синхронно: детали из списка (by-module-id или detailPatterns) без GET paced-detail-modules/{id}. */
export const buildEmbeddedModuleChildrenFromDetails = (details) => {
    const stamped = stampCompositionDetailList(Array.isArray(details) ? details : []);
    const sorted = sortDetailsBySequenceNumber(stamped);
    const children = sorted
        .map((detail, index) => {
            const child = buildEmbeddedChildFromDetail(detail, index);
            if (!child) {
                return null;
            }
            return {
                ...child,
                sceneDrawOrder: getDetailSceneDrawOrder(detail, index),
            };
        })
        .filter(Boolean);
    return {children, detailsCount: stamped.length};
};

/**
 * Один GET by-module-id на встраиваемый модуль; pacedDetailModule из ответа — без
 * отдельных paced-detail-modules/{id} на каждую деталь.
 */
export const loadEmbeddedModuleChildren = async (dependModuleId, {bypassCache = false} = {}) => {
    const {details, loaded, notFound} = await loadEmbeddedModuleDetails(dependModuleId, {bypassCache});
    if (!loaded) {
        return {children: [], detailsCount: 0, loaded: false, notFound};
    }
    const {children, detailsCount} = buildEmbeddedModuleChildrenFromDetails(details);
    return {children, detailsCount, loaded: true, notFound};
};

const buildEmbeddedInstanceCore = ({
    row,
    index = 0,
    localPosition,
    axes,
    faceFlip = false,
    children = [],
    pacedModule = null,
    instanceId = null,
}) => {
    const normalizedRow = normalizeDependModuleRow(row);
    const moduleKey = getDependModuleKey(normalizedRow, index);
    const pacedId = pacedModule ? getPacedModuleId({pacedModule}) : null;
    const moduleSizeM = moduleDimensionsMmToSizeM(
        normalizedRow?.width,
        normalizedRow?.height,
        normalizedRow?.depth,
    );
    const resolvedAxes = axes || IDENTITY_AXES;

    return {
        instanceId: instanceId
            ?? (pacedId != null ? `embedded-paced-${pacedId}` : `embedded-${moduleKey}`),
        moduleKey,
        dependModuleId: getDependModuleId(normalizedRow),
        label: normalizedRow?.name,
        row: normalizedRow,
        localPosition,
        axes: resolvedAxes,
        faceFlip: Boolean(faceFlip),
        euler: axesToEulerDegrees(resolvedAxes),
        moduleSizeM,
        children,
        pacedModuleId: pacedId,
        pacedModule,
        hidden: getPacedDtoHide(pacedModule ?? normalizedRow?.pacedModule),
        childrenHydrated: false,
    };
};

/** Синхронно: зелёный куб и поза, без деталей внутри (детали подгружаются отдельно). */
export const buildPlacedEmbeddedInstanceShellFromPose = ({
    row,
    index = 0,
    localPosition,
    axes,
    faceFlip = false,
    pacedModule = null,
    instanceId = null,
}) => {
    if (!row || !localPosition) return null;
    return buildEmbeddedInstanceCore({
        row,
        index,
        localPosition,
        axes: axes || IDENTITY_AXES,
        faceFlip,
        children: [],
        pacedModule,
        instanceId,
    });
};

export const buildPlacedEmbeddedInstanceFromPose = async ({
    row,
    index = 0,
    localPosition,
    axes,
    faceFlip = false,
    pacedModule = null,
    instanceId = null,
}) => {
    const shell = buildPlacedEmbeddedInstanceShellFromPose({
        row,
        index,
        localPosition,
        axes,
        faceFlip,
        pacedModule,
        instanceId,
    });
    if (!shell) return null;
    const {children, detailsCount} = await loadEmbeddedModuleChildren(getDependModuleId(row));
    return {
        ...shell,
        children,
        childrenHydrated: true,
        embeddedDetailsCount: detailsCount,
        embeddedDetailsLoaded: true,
    };
};

export const buildPlacedEmbeddedInstance = async (row, index = 0) => {
    const paced = parsePacedModuleDto(row?.pacedModule);
    if (!row || !paced?.localPosition) return null;

    return buildPlacedEmbeddedInstanceFromPose({
        row,
        index,
        localPosition: paced.localPosition,
        axes: paced.axes || IDENTITY_AXES,
        faceFlip: paced.faceFlip,
        pacedModule: row.pacedModule,
    });
};

/** Модуль уже размещён на сцене (есть anchor / localPosition в pacedModule). */
export const isEmbeddedModulePlacedDto = (row) => {
    const paced = parsePacedModuleDto(row?.pacedModule);
    return Boolean(paced?.localPosition);
};

/** Поза встраиваемого модуля — pacedModule из dependModules в GET родителя. */
export const resolveDependModuleRowWithPlacement = (row) => normalizeDependModuleRow(row);

export const buildPlacedEmbeddedInstanceIfPlaced = (row, index = 0) => {
    const rowWithPlacement = resolveDependModuleRowWithPlacement(row);
    if (!isEmbeddedModulePlacedDto(rowWithPlacement)) {
        return {inst: null, rowWithPlacement};
    }
    const paced = parsePacedModuleDto(rowWithPlacement?.pacedModule);
    const inst = buildPlacedEmbeddedInstanceShellFromPose({
        row: rowWithPlacement,
        index,
        localPosition: paced.localPosition,
        axes: paced.axes || IDENTITY_AXES,
        faceFlip: paced.faceFlip,
        pacedModule: rowWithPlacement.pacedModule,
    });
    return {inst, rowWithPlacement};
};

/** Габариты для snap при клике по сцене (как detailData панели). */
export const embeddedModulePlacementDetailData = (row) => ({
    length: Math.max(Number(row?.width) || 0, 1),
    width: Math.max(Number(row?.depth) || 0, 1),
    height: Math.max(Number(row?.height) || 0, 1),
});
